// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// view.ts — the play screen. DOM, not canvas: this is a board game made of text and rectangles, and
// the DOM gives crisp numerals, 44px hit areas and a responsive layout for free.
//
// TWO IDEAS CARRY THE WHOLE PHONE LAYOUT.
//
// 1. A RACK IS A PROPORTIONAL BAR, NOT FOUR LOOSE TILES. A rack holding two Cinnabar, a Verdigris
//    and a Marl is one bar sliced 2:1:1. Three things fall out at once: area is proportional to
//    count, so you READ counts instead of counting pips; the tap target IS the legal move (a kind in
//    a rack), not a component of one; and the grid is frozen at round start, so a spent rack leaves
//    a recessed husk in the same place all round and the husks are a free progress bar with no
//    reflow at all.
//
// 2. SELECTION IS FREE. Tapping a slice commits nothing — it opens a sheet that spells out every
//    destination, what it scores, what it costs you, and who you are about to help or hurt. That is
//    the one thing a screen can do that a table cannot, and it is why a 40px slice is an acceptable
//    target when a 44px floor is the rule: nothing irreversible is ever one tap away.

import { COURSES, KINDS, KIND_GLYPHS, KIND_NAMES, SIDE, SPOIL_SLOTS, courseCap, printedKind, SPOIL_LADDER } from './kinds';
import { destinations, columnRead, readTake, type Dest } from './read';
import { SILL, SPOIL, Table } from './rules';

export const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

export interface Seat {
  readonly name: string;
  readonly bot: boolean;
}

/** A tile face: colour AND glyph, always both. */
export function tile(kind: number, extra = '', label = ''): string {
  return `<span class="tile k${kind}" ${label ? `aria-label="${esc(label)}"` : 'aria-hidden="true"'} ${extra}><span class="glyph">${KIND_GLYPHS[kind]}</span></span>`;
}

const empty = (cls = ''): string => `<span class="slot ${cls}"></span>`;

// ---------------------------------------------------------------- the draft

export function racksHtml(t: Table, selected: { src: number; kind: number } | null): string {
  const out: string[] = [];
  for (let r = 0; r < t.rackCount; r++) {
    const total = t.rackTiles(r);
    if (total === 0) {
      out.push(`<div class="rack husk" aria-hidden="true"><span class="husk-mark"></span></div>`);
      continue;
    }
    const slices: string[] = [];
    for (let k = 0; k < KINDS; k++) {
      const n = t.racks[r * KINDS + k];
      if (n === 0) continue;
      const on = selected && selected.src === r && selected.kind === k;
      slices.push(
        `<button class="slice k${k}${on ? ' on' : ''}" style="flex-grow:${n}" data-src="${r}" data-kind="${k}"` +
          ` aria-label="Take ${n} ${esc(KIND_NAMES[k])} from rack ${r + 1}">` +
          `<span class="glyph">${KIND_GLYPHS[k]}</span><span class="n">${n}</span></button>`,
      );
    }
    out.push(`<div class="rack">${slices.join('')}</div>`);
  }
  return out.join('');
}

export function sillHtml(t: Table, selected: { src: number; kind: number } | null): string {
  const chips: string[] = [];
  for (let k = 0; k < KINDS; k++) {
    const n = t.sill[k];
    const on = selected && selected.src === SILL && selected.kind === k;
    if (n === 0) {
      chips.push(`<span class="chip out k${k}" aria-hidden="true"><span class="glyph">${KIND_GLYPHS[k]}</span></span>`);
      continue;
    }
    chips.push(
      `<button class="chip k${k}${on ? ' on' : ''}" data-src="${SILL}" data-kind="${k}"` +
        ` aria-label="Take ${n} ${esc(KIND_NAMES[k])} from the sill"><span class="glyph">${KIND_GLYPHS[k]}</span><span class="n">${n}</span></button>`,
    );
  }
  chips.push(
    t.trowelInSill
      ? `<span class="chip trowel" title="The first player to take from the sill picks this up: one point off, and they open the next round.">⌁</span>`
      : `<span class="chip trowel gone" aria-hidden="true">⌁</span>`,
  );
  return `<div class="sill-row">${chips.join('')}</div>`;
}

// ---------------------------------------------------------------- a player board

export function courseHtml(t: Table, seat: number, row: number): string {
  const i = seat * COURSES + row;
  const cap = courseCap(row);
  const kind = t.courseKind[i];
  const n = t.courseCount[i];
  const cells: string[] = [];
  // Right-aligned, so the fill edge meets the Mosaic: that alignment is the only teaching this
  // board does, and it is free.
  for (let c = 0; c < SIDE; c++) {
    const idx = c - (SIDE - cap);
    if (idx < 0) cells.push(empty('void'));
    else if (idx >= cap - n) cells.push(tile(kind));
    else cells.push(empty());
  }
  const locked = (t.fired[seat] >> row) & 1 ? ' locked' : '';
  return `<div class="course${locked}" data-row="${row}">${cells.join('')}</div>`;
}

export function mosaicHtml(t: Table, seat: number): string {
  const rows: string[] = [];
  for (let r = 0; r < SIDE; r++) {
    const cells: string[] = [];
    for (let c = 0; c < SIDE; c++) {
      const k = t.cell(seat, r, c);
      if (k >= 0) {
        cells.push(tile(k, '', `${KIND_NAMES[k]} at row ${r + 1} column ${c + 1}`));
      } else if (t.mode.printed) {
        cells.push(`<span class="slot ghost k${printedKind(r, c)}"><span class="glyph">${KIND_GLYPHS[printedKind(r, c)]}</span></span>`);
      } else {
        // Freehand: a reservation renders as a ghost, because your plan is public.
        const i = seat * COURSES + r;
        const reserved = t.courseCol[i] === c && t.courseKind[i] >= 0;
        cells.push(
          reserved
            ? `<span class="slot ghost res k${t.courseKind[i]}"><span class="glyph">${KIND_GLYPHS[t.courseKind[i]]}</span></span>`
            : empty(),
        );
      }
    }
    rows.push(`<div class="mrow">${cells.join('')}</div>`);
  }
  return rows.join('');
}

export function spoilHtml(t: Table, seat: number): string {
  const items = t.spoilTiles[seat] + t.trowel[seat];
  const cells: string[] = [];
  for (let i = 0; i < SPOIL_SLOTS; i++) {
    const filled = i < items;
    cells.push(
      `<span class="sp${filled ? ' on' : ''}"><span class="pen">−${SPOIL_LADDER[i]}</span></span>`,
    );
  }
  const over = items > SPOIL_SLOTS ? `<span class="sp-over">+${items - SPOIL_SLOTS} free</span>` : '';
  return `<div class="spoil-row" aria-label="Spoil">${cells.join('')}${over}</div>`;
}

export function boardHtml(t: Table, seat: number, seats: readonly Seat[], self: boolean): string {
  const courses: string[] = [];
  for (let r = 0; r < COURSES; r++) courses.push(courseHtml(t, seat, r));
  return (
    `<div class="board${self ? ' self' : ''}">` +
    `<div class="board-head"><span class="who">${esc(seats[seat]?.name ?? `Seat ${seat + 1}`)}</span>` +
    `<span class="score">${Math.max(0, t.score[seat])}</span></div>` +
    `<div class="board-grid"><div class="courses">${courses.join('')}</div>` +
    `<div class="mosaic">${mosaicHtml(t, seat)}</div></div>` +
    spoilHtml(t, seat) +
    `</div>`
  );
}

// ---------------------------------------------------------------- the rival rail

/**
 * One card per rival, and it is LOSSLESS: five course chips, one per course, in fixed row order.
 * A summary that showed only the three most urgent courses would hide 40% of the state a human needs
 * in the one dimension the game is about, while the bot saw all of it.
 */
export function railHtml(t: Table, self: number, seats: readonly Seat[]): string {
  const cards: string[] = [];
  for (let s = 0; s < t.players; s++) {
    if (s === self) continue;
    const chips: string[] = [];
    for (let c = 0; c < COURSES; c++) {
      const i = s * COURSES + c;
      const k = t.courseKind[i];
      const need = courseCap(c) - t.courseCount[i];
      if (k < 0) chips.push(`<span class="cc empty">${c + 1}</span>`);
      else if ((t.fired[s] >> c) & 1) chips.push(`<span class="cc done">✓</span>`);
      else chips.push(`<span class="cc k${k}"><span class="glyph">${KIND_GLYPHS[k]}</span><span class="need">${need}</span></span>`);
    }
    const items = t.spoilTiles[s] + t.trowel[s];
    cards.push(
      `<button class="rival${t.seat === s ? ' active' : ''}" data-seat="${s}" aria-label="Look at ${esc(seats[s]?.name ?? '')}'s board">` +
        `<span class="rv-head"><span class="rv-name">${esc(seats[s]?.name ?? `Seat ${s + 1}`)}</span>` +
        `<span class="rv-score">${Math.max(0, t.score[s])}</span></span>` +
        `<span class="rv-chips">${chips.join('')}</span>` +
        (items > 0 ? `<span class="rv-spoil">−${SPOIL_LADDER.slice(0, Math.min(items, SPOIL_SLOTS)).reduce((a, b) => a + b, 0)}</span>` : '') +
        `</button>`,
    );
  }
  return cards.join('');
}

// ---------------------------------------------------------------- the move sheet

function destRow(t: Table, d: Dest, kind: number): string {
  const label =
    d.dest === SPOIL
      ? 'Spoil'
      : `Course ${d.dest + 1}<span class="cap">${t.courseCount[t.seat * COURSES + d.dest]}/${courseCap(d.dest)}</span>`;
  if (!d.legal) {
    return `<button class="drow off" disabled aria-disabled="true"><span class="dl">${label}</span><span class="dv">${esc(d.reason)}</span></button>`;
  }
  const bits: string[] = [];
  if (d.fills) bits.push(`<span class="plus">+${d.lay}</span>`);
  else if (d.dest !== SPOIL) bits.push(`<span class="hold">holds ${d.placed}</span>`);
  if (d.cost < 0) bits.push(`<span class="minus">${d.cost}</span>`);
  if (d.dest === SPOIL && d.cost === 0) bits.push(`<span class="hold">free</span>`);
  return (
    `<button class="drow" data-dest="${d.dest}" data-kind="${kind}">` +
    `<span class="dl">${label}</span><span class="dv">${bits.join('')}</span></button>`
  );
}

export function sheetHtml(t: Table, seat: number, src: number, kind: number): string {
  const r = readTake(t, seat, src, kind);
  const where = src === SILL ? 'the sill' : `rack ${src + 1}`;
  const names = (ts: readonly { seat: number }[]): string =>
    ts.slice(0, 2).map((g) => esc(nameOfSeat(g.seat))).join(' and ');

  // Two separate sentences, because they are two separate facts and running them together produced
  // "leaves ◆×1 in the sill → Kestrel, Vane, Rook takes it from Kestrel, Vane, Rook", which reads as
  // gibberish. The gift line only appears when there is something to give.
  const lines: string[] = [];
  if (r.leaves.length) {
    const leaves = r.leaves.map((l) => `${KIND_GLYPHS[l.kind]}×${l.count}`).join(' ');
    lines.push(
      `<span class="src-line">Leaves ${leaves} in the sill` +
        (r.gifts.length ? ` <span class="warn">— ${names(r.gifts)} want that</span>` : '') +
        `</span>`,
    );
  } else if (src !== SILL) {
    lines.push(`<span class="src-line">Leaves nothing behind — the rack empties.</span>`);
  }
  if (r.denies.length) {
    lines.push(`<span class="src-line"><span class="good">Takes it from ${names(r.denies)}</span></span>`);
  }
  if (src === SILL && t.trowelInSill) {
    lines.push(`<span class="src-line"><span class="warn">Picks up the trowel: −1, and you open the next round.</span></span>`);
  }

  const dests = destinations(t, seat, src, kind);
  return (
    `<div class="sheet-inner" role="dialog" aria-modal="true" aria-label="Where do these go?">` +
    `<div class="sheet-src">` +
    `<span class="src-take">${tile(kind)}<b>×${r.count}</b> <span class="src-where">from ${esc(where)}</span></span>` +
    lines.join('') +
    `</div>` +
    `<div class="sheet-rows">${dests.map((d) => destRow(t, d, kind)).join('')}</div>` +
    `<button class="sheet-cancel" data-cancel="1">Cancel</button>` +
    `</div>`
  );

  function nameOfSeat(s: number): string {
    return seatNames[s] ?? `Seat ${s + 1}`;
  }
}

let seatNames: string[] = [];
export const setSeatNames = (names: string[]): void => {
  seatNames = names;
};

/**
 * Freehand's column step REPLACES the destination rows rather than stacking under them. Stacked, the
 * sheet would be ~570px on a 585px budget and would cover the very Mosaic whose cell you are
 * choosing; and a destination row cannot print a projected score in Freehand anyway, because the
 * score depends on the column you have not picked yet. Here the number is on the chip, where it
 * belongs — along with what the nomination forecloses, which is the trap a newcomer cannot see.
 */
export function columnStripHtml(t: Table, seat: number, course: number, kind: number): string {
  const cols = t.legalCols(seat, course, kind);
  const chips: string[] = [];
  for (let c = 0; c < SIDE; c++) {
    const ok = cols.includes(c);
    if (!ok) {
      chips.push(`<button class="colchip off" disabled aria-disabled="true">${c + 1}</button>`);
      continue;
    }
    const { lay, blocks } = columnRead(t, seat, course, kind, c);
    chips.push(
      `<button class="colchip" data-col="${c}" aria-label="Column ${c + 1}, worth ${lay}">` +
        `<span class="cnum">${c + 1}</span><span class="clay">+${lay}</span>` +
        (blocks.length ? `<span class="cblock">blocks ${blocks.length}</span>` : '') +
        `</button>`,
    );
  }
  // The row you are choosing into, drawn right here. The sheet is deliberately short so the Mosaic
  // stays on screen, but on a 4-player phone the lower rows still sit under it — and "which cell am
  // I committing to" is the entire question this step is asking. So the row comes to the sheet.
  const preview: string[] = [];
  for (let c = 0; c < SIDE; c++) {
    const k = t.cell(seat, course, c);
    preview.push(k >= 0 ? tile(k) : `<span class="slot ${cols.includes(c) ? 'open' : 'shut'}"></span>`);
  }

  return (
    `<div class="sheet-inner" role="dialog" aria-modal="true" aria-label="Which column?">` +
    `<div class="sheet-src"><span class="src-take">${tile(kind)} into course ${course + 1}</span>` +
    `<span class="src-line">Name its column now — you are committing to that cell.</span></div>` +
    `<div class="colrow" aria-hidden="true">${preview.join('')}</div>` +
    `<div class="colstrip">${chips.join('')}</div>` +
    `<button class="sheet-cancel" data-cancel="1">Back</button>` +
    `</div>`
  );
}
